import { useState } from 'react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import type { PadelGameListItem } from '@/services/padelService';
import { americanoScoreExample, resolveAmericanoScores } from './padelScoringUtils';
import { playersFirstNameLabel } from './padelUiUtils';
import PadelScorePickerSheet from './PadelScorePickerSheet';

interface PadelMatchScorePanelProps {
  game: PadelGameListItem;
  scoreA: number;
  scoreB: number;
  canEdit: boolean;
  onChange: (scoreA: number, scoreB: number) => void;
}

type PickerSide = 'a' | 'b' | null;

export default function PadelMatchScorePanel({
  game,
  scoreA,
  scoreB,
  canEdit,
  onChange,
}: PadelMatchScorePanelProps) {
  const [pickerSide, setPickerSide] = useState<PickerSide>(null);

  const firstNameA = playersFirstNameLabel(game.sideAPlayers) || 'Tim A';
  const firstNameB = playersFirstNameLabel(game.sideBPlayers) || 'Tim B';
  const targetScore = game.targetScore;
  const americanoMode = game.scoringType === 'points' && targetScore != null;
  const timeHint =
    game.scoringType === 'time' && targetScore != null ? `${targetScore} menit (informasi)` : null;
  const rallyTotal = scoreA + scoreB;
  const rallyTargetValid = americanoMode && rallyTotal === targetScore;

  const handleScoreSelect = (side: 'a' | 'b', newScore: number) => {
    if (!canEdit) return;
    if (americanoMode && targetScore != null) {
      const resolved = resolveAmericanoScores(side, newScore, scoreA, scoreB, targetScore);
      onChange(resolved.scoreA, resolved.scoreB);
      return;
    }
    if (side === 'a') onChange(newScore, scoreB);
    else onChange(scoreA, newScore);
  };

  const pickerOpen = pickerSide !== null;
  const pickerValue = pickerSide === 'a' ? scoreA : pickerSide === 'b' ? scoreB : 0;
  const pickerLabel = pickerSide === 'a' ? firstNameA : pickerSide === 'b' ? firstNameB : '';

  return (
    <div className="space-y-4">
      {timeHint && (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">{timeHint}</p>
      )}
      {americanoMode && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            rallyTargetValid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          <p className="font-medium">
            Total rally: {rallyTotal} / {targetScore}
          </p>
          {!rallyTargetValid && (
            <p className="mt-0.5 text-xs">
              Contoh skor valid: {americanoScoreExample(targetScore)}
            </p>
          )}
        </div>
      )}

      {/* Mobile Section */}
      <div className="space-y-2 py-1 lg:hidden">
        <TeamScoreRow
          label={firstNameA}
          value={scoreA}
          disabled={!canEdit}
          onOpen={() => setPickerSide('a')}
        />
        <div className="flex items-center justify-center gap-2">
          <span className="h-px flex-1 bg-gray-100" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">vs</span>
          <span className="h-px flex-1 bg-gray-100" />
        </div>
        <TeamScoreRow
          label={firstNameB}
          value={scoreB}
          disabled={!canEdit}
          onOpen={() => setPickerSide('b')}
        />
      </div>

      {/* Desktop Section */}
      <div className="hidden items-center justify-center gap-10 py-4 lg:flex">
        <TeamScore
          label={firstNameA}
          value={scoreA}
          disabled={!canEdit}
          onOpen={() => setPickerSide('a')}
        />
        <span className="text-2xl font-bold text-gray-400">vs</span>
        <TeamScore
          label={firstNameB}
          value={scoreB}
          disabled={!canEdit}
          onOpen={() => setPickerSide('b')}
        />
      </div>

      <PadelScorePickerSheet
        open={pickerOpen}
        onClose={() => setPickerSide(null)}
        teamLabel={pickerLabel}
        value={pickerValue}
        targetScore={targetScore}
        americanoMode={americanoMode}
        onSelect={(score) => {
          if (pickerSide) handleScoreSelect(pickerSide, score);
        }}
      />
    </div>
  );
}

function TeamScoreRow({
  label,
  value,
  disabled,
  onOpen,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onOpen: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{label}</p>
      <ScoreDisplay value={value} disabled={disabled} onOpen={onOpen} size="sm" />
    </div>
  );
}

function TeamScore({
  label,
  value,
  disabled,
  onOpen,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onOpen: () => void;
}) {
  return (
    <div className="items-center justify-center text-center">
      <p className="mb-2 truncate text-sm font-medium text-gray-700">{label}</p>
      <ScoreDisplay value={value} disabled={disabled} onOpen={onOpen} size="lg" />
    </div>
  );
}

function ScoreDisplay({
  value,
  disabled,
  onOpen,
  size,
}: {
  value: number;
  disabled: boolean;
  onOpen: () => void;
  size: 'sm' | 'lg';
}) {
  const sizeClass = size === 'lg' ? 'h-14 w-20 text-3xl' : 'h-10 w-14 text-xl';

  if (disabled) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-md bg-white font-bold text-gray-900 ${sizeClass}`}
      >
        {value}
      </span>
    );
  }

  return (
    <ShadcnButton
      type="button"
      variant="outline"
      onClick={onOpen}
      className={`rounded-md border-gray-200 bg-white font-bold text-gray-900 hover:bg-gray-50 ${sizeClass}`}
    >
      {value}
    </ShadcnButton>
  );
}
